import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { FiPlus, FiTrash2, FiChevronUp, FiChevronDown, FiFileText } from 'react-icons/fi';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import ReportTemplatePreviewModal from './ReportTemplatePreviewModal';
import AssignReportModal from './AssignReportModal';
import '../../styles/TemplatesManagement.css';

const TemplatesManagement = ({ templates: initialTemplates, schoolId, onTemplatesChange }) => {
    const { t } = useTranslation('reports');
    const [templates, setTemplates] = useState(initialTemplates || []);

    // Modal states
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

    // Selected items
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const [templateToPreview, setTemplateToPreview] = useState(null);
    const [templateToAssign, setTemplateToAssign] = useState(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Template form state
    const [templateForm, setTemplateForm] = useState({
        title: '',
        questions: []
    });
    const [newQuestionText, setNewQuestionText] = useState('');

    // Sync templates with parent
    useEffect(() => {
        setTemplates(initialTemplates || []);
    }, [initialTemplates]);

    const updateTemplates = (newTemplates) => {
        setTemplates(newTemplates);
        if (onTemplatesChange) {
            onTemplatesChange(newTemplates);
        }
    };

    const resetTemplateForm = () => {
        setTemplateForm({ title: '', questions: [] });
        setNewQuestionText('');
        setError('');
    };

    const handleTitleChange = (e) => {
        setTemplateForm({ ...templateForm, title: e.target.value });
    };

    const addQuestionToTemplate = () => {
        if (!newQuestionText.trim()) {
            setError(t('questionTextRequired'));
            return;
        }
        setTemplateForm({
            ...templateForm,
            questions: [...templateForm.questions, {
                questionText: newQuestionText.trim(),
                orderIndex: templateForm.questions.length
            }]
        });
        setNewQuestionText('');
        setError('');
    };

    const handleQuestionKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            addQuestionToTemplate();
        }
    };

    const removeQuestion = (index) => {
        const updatedQuestions = templateForm.questions.filter((_, i) => i !== index);
        updatedQuestions.forEach((q, i) => q.orderIndex = i);
        setTemplateForm({ ...templateForm, questions: updatedQuestions });
    };

    const moveQuestion = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= templateForm.questions.length) return;
        const updatedQuestions = [...templateForm.questions];
        const temp = updatedQuestions[index];
        updatedQuestions[index] = updatedQuestions[newIndex];
        updatedQuestions[newIndex] = temp;
        updatedQuestions.forEach((q, i) => q.orderIndex = i);
        setTemplateForm({ ...templateForm, questions: updatedQuestions });
    };

    // Create template
    const handleCreateTemplate = async () => {
        if (!templateForm.title.trim()) {
            setError(t('templateTitleRequired'));
            return;
        }
        if (templateForm.questions.length === 0) {
            setError(t('templateNeedsQuestion'));
            return;
        }

        try {
            setLoading(true);
            const templateQuestions = templateForm.questions.map(q => ({
                questionText: q.questionText,
                orderIndex: q.orderIndex
            }));

            const response = await axios.post('/api/v1/reports/templates', {
                schoolId,
                title: templateForm.title,
                questions: templateQuestions
            });

            updateTemplates([...templates, response.data]);
            setIsCreateModalOpen(false);
            resetTemplateForm();
            showNotification(t('templateCreated'));
        } catch (err) {
            setError(t('createTemplateError'));
        } finally {
            setLoading(false);
        }
    };

    // Delete template
    const handleDeleteTemplate = async () => {
        try {
            setLoading(true);
            await axios.delete(`/api/v1/reports/templates/${templateToDelete.id}`);
            updateTemplates(templates.filter(t => t.id !== templateToDelete.id));
            setIsDeleteModalOpen(false);
            setTemplateToDelete(null);
            setError('');
            showNotification(t('templateDeleted'));
        } catch (err) {
            setError(t('deleteTemplateError'));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteFromPreview = (template) => {
        setIsPreviewModalOpen(false);
        setTemplateToPreview(null);
        setTemplateToDelete(template);
        setIsDeleteModalOpen(true);
    };

    const openPreviewModal = (template) => {
        setTemplateToPreview(template);
        setIsPreviewModalOpen(true);
    };

    const openAssignModal = (template) => {
        setTemplateToAssign(template);
        setIsAssignModalOpen(true);
    };

    const showNotification = (message, type = 'success') => {
        const notification = document.createElement('div');
        notification.className = type === 'success' ? 'tm-success-notification' : 'tm-error-notification';
        notification.textContent = message;
        document.body.appendChild(notification);
        setTimeout(() => {
            if (document.body.contains(notification)) {
                document.body.removeChild(notification);
            }
        }, 3000);
    };

    useEffect(() => {
        if (error) {
            showNotification(error, 'error');
            setError('');
        }
    }, [error]);

    return (
        <div className="tm-container">
            {/* Header with Create Button */}
            <div className="tm-header">
                <button
                    className="tm-create-btn"
                    onClick={() => setIsCreateModalOpen(true)}
                >
                    {t('createTemplate')}
                </button>
            </div>

            {/* Templates Grid */}
            {templates.length === 0 ? (
                <div className="tm-empty-state">
                    <FiFileText className="tm-empty-icon" />
                    <p>{t('noTemplates')}</p>
                    <button
                        className="tm-create-btn"
                        onClick={() => setIsCreateModalOpen(true)}
                    >
                        {t('createFirstTemplate')}
                    </button>
                </div>
            ) : (
                <div className="tm-grid">
                    {templates.map((template) => (
                        <div key={template.id} className="tm-card">
                            <div className="tm-card-header">
                                <div className="tm-card-title-section">
                                    <h3 className="tm-card-title">{template.title}</h3>
                                    <p className="tm-card-count">
                                        {t('questionsCount', { count: template.questions?.length || 0 })}
                                    </p>
                                </div>
                                <button
                                    className="tm-card-delete-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setTemplateToDelete(template);
                                        setIsDeleteModalOpen(true);
                                    }}
                                    title={t('deleteTemplate')}
                                >
                                    <FiTrash2 />
                                </button>
                            </div>

                            <div className="tm-card-actions">
                                <button
                                    className="tm-btn-preview"
                                    onClick={() => openPreviewModal(template)}
                                >
                                    {t('preview')}
                                </button>
                                <button
                                    className="tm-btn-assign"
                                    onClick={() => openAssignModal(template)}
                                >
                                    {t('assign')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Template Modal */}
            {isCreateModalOpen && (
                <div className="tm-modal-overlay" onClick={() => { setIsCreateModalOpen(false); resetTemplateForm(); }}>
                    <div className="tm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="tm-modal-header">
                            <h2 className="tm-modal-title">{t('createNewTemplate')}</h2>
                        </div>

                        <div className="tm-modal-content">
                            <div className="tm-input-group">
                                <label htmlFor="templateTitle">{t('templateTitle')}</label>
                                <input
                                    id="templateTitle"
                                    type="text"
                                    value={templateForm.title}
                                    onChange={handleTitleChange}
                                    placeholder={t('templateTitlePlaceholder')}
                                    className="tm-input"
                                />
                            </div>

                            <div className="tm-questions-section">
                                <h3 className="tm-section-title">{t('questionsInTemplate')}</h3>

                                {/* Questions List */}
                                <div className="tm-questions-list">
                                    {templateForm.questions.length === 0 ? (
                                        <div className="tm-no-questions">
                                            {t('noQuestionsAdded')}
                                        </div>
                                    ) : (
                                        templateForm.questions.map((question, index) => (
                                            <div key={index} className="tm-question-item">
                                                <div className="tm-question-header">
                                                    <span className="tm-question-number">{t('questionNumber', { number: index + 1 })}</span>
                                                    <div className="tm-question-controls">
                                                        <button
                                                            type="button"
                                                            className="tm-move-btn"
                                                            onClick={() => moveQuestion(index, 'up')}
                                                            disabled={index === 0}
                                                        >
                                                            <FiChevronUp />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tm-move-btn"
                                                            onClick={() => moveQuestion(index, 'down')}
                                                            disabled={index === templateForm.questions.length - 1}
                                                        >
                                                            <FiChevronDown />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tm-remove-btn"
                                                            onClick={() => removeQuestion(index)}
                                                        >
                                                            <FiTrash2 />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="tm-question-content">
                                                    <p className="tm-question-text">{question.questionText}</p>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* Add Question - Simple textarea + button */}
                                <div className="tm-add-question-section">
                                    <h4>{t('addNewQuestion')}</h4>
                                    <div className="tm-question-input-area">
                                        <div className="tm-input-group">
                                            <textarea
                                                value={newQuestionText}
                                                onChange={(e) => setNewQuestionText(e.target.value)}
                                                onKeyDown={handleQuestionKeyDown}
                                                placeholder={t('questionTextPlaceholder')}
                                                rows="3"
                                                className="tm-textarea"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="tm-add-question-btn"
                                            onClick={addQuestionToTemplate}
                                            disabled={!newQuestionText.trim()}
                                        >
                                            <FiPlus /> {t('addToTemplate')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="tm-modal-footer">
                            <button
                                className="tm-btn-cancel"
                                onClick={() => {
                                    setIsCreateModalOpen(false);
                                    resetTemplateForm();
                                }}
                                disabled={loading}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                className="tm-btn-submit"
                                onClick={handleCreateTemplate}
                                disabled={loading || !templateForm.title.trim() || templateForm.questions.length === 0}
                            >
                                {loading ? t('creating') : t('createTemplate')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            <ReportTemplatePreviewModal
                isOpen={isPreviewModalOpen}
                onClose={() => {
                    setIsPreviewModalOpen(false);
                    setTemplateToPreview(null);
                }}
                template={templateToPreview}
                onDelete={handleDeleteFromPreview}
            />

            {/* Assignment Modal */}
            <AssignReportModal
                isOpen={isAssignModalOpen}
                onClose={() => {
                    setIsAssignModalOpen(false);
                    setTemplateToAssign(null);
                }}
                templates={templates}
                preselectedTemplateId={templateToAssign?.id}
                schoolId={schoolId}
                onAssigned={() => {
                    setIsAssignModalOpen(false);
                    setTemplateToAssign(null);
                }}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setTemplateToDelete(null);
                }}
                onConfirm={handleDeleteTemplate}
                itemName={`szablon "${templateToDelete?.title}"`}
            />
        </div>
    );
};

export default TemplatesManagement;
